import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, rupee } from "../lib/api";
import { todayInputValue } from "../lib/saleInvoice";
import { toast } from "sonner";

function paymentToForm(payment, sale) {
  if (payment) {
    const at = payment.received_at || "";
    return {
      amount: String(payment.amount ?? ""),
      payment_method: payment.payment_method || "cash",
      received_at: at.slice(0, 10) || todayInputValue(),
      notes: payment.notes || "",
    };
  }
  return {
    amount: String(sale?.total ?? ""),
    payment_method: sale?.payment_method && sale.payment_method !== "pending" ? sale.payment_method : "cash",
    received_at: (sale?.created_at || "").slice(0, 10) || todayInputValue(),
    notes: "",
  };
}

export default function EditPaymentReceiptModal({
  open,
  onOpenChange,
  sale,
  payment,
  onSaved,
}) {
  const [form, setForm] = useState(() => paymentToForm(payment, sale));
  const [saving, setSaving] = useState(false);
  const isLegacy = !payment?.id;

  useEffect(() => {
    if (open) {
      setForm(paymentToForm(payment, sale));
    }
  }, [open, payment, sale]);

  const otherPaid = (sale?.payments || [])
    .filter((p) => p.id && p.id !== payment?.id)
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const maxAmount = Math.max(0.01, (Number(sale?.total) || 0) - otherPaid);

  const save = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    if (amount > maxAmount + 0.009) {
      return toast.error(`Amount cannot exceed ${rupee(maxAmount)} for this invoice`);
    }

    setSaving(true);
    try {
      const payload = {
        amount,
        payment_method: form.payment_method,
        received_at: form.received_at,
        notes: form.notes.trim() || undefined,
      };

      let data;
      if (isLegacy) {
        const res = await api.post(`/sales/${sale.id}/payments`, payload);
        data = res.data;
      } else {
        const res = await api.put(`/sales/${sale.id}/payments/${payment.id}`, payload);
        data = res.data;
      }

      toast.success(isLegacy ? "Payment receipt recorded" : "Payment receipt updated");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save receipt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isLegacy ? "Record payment receipt" : "Edit payment receipt"}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Invoice <span className="font-mono font-semibold">{sale?.invoice_no}</span>
          {sale?.total != null && <> · total {rupee(sale.total)}</>}
        </p>

        <form onSubmit={save} className="space-y-4 mt-2">
          <div>
            <label className="text-sm font-medium">Amount ₹</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={maxAmount}
              required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-1 h-11 w-full px-3 rounded-md border bg-white num"
              style={{ borderColor: "var(--border-soft)" }}
              data-testid="edit-receipt-amount"
            />
            {otherPaid > 0 && (
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                Max {rupee(maxAmount)} (other receipts total {rupee(otherPaid)})
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Payment mode</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {["cash", "upi", "card"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm({ ...form, payment_method: m })}
                  className="h-10 rounded-md text-sm font-semibold border uppercase"
                  style={{
                    borderColor: form.payment_method === m ? "var(--primary)" : "var(--border-soft)",
                    background: form.payment_method === m ? "var(--drag-highlight)" : "white",
                    color: form.payment_method === m ? "var(--primary)" : "inherit",
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Date of receipt</label>
            <input
              type="date"
              required
              value={form.received_at}
              onChange={(e) => setForm({ ...form, received_at: e.target.value })}
              className="mt-1 h-11 w-full px-3 rounded-md border bg-white"
              style={{ borderColor: "var(--border-soft)" }}
              data-testid="edit-receipt-date"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Notes</label>
            <input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional"
              className="mt-1 h-11 w-full px-3 rounded-md border bg-white"
              style={{ borderColor: "var(--border-soft)" }}
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full h-12 rounded-md text-white font-semibold tactile-btn"
            style={{ background: "var(--primary)" }}
            data-testid="edit-receipt-save"
          >
            {saving ? "Saving…" : "Save receipt"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
